// Admin authentication for the /admin panel. A single operator logs in; the
// signed token is stored in localStorage and attached to every axios request as
// a Bearer header. A 401 interceptor clears the token and bounces to login.
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';
const TOKEN_KEY = 'admin_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function isAuthed(): boolean {
  return !!getToken();
}

function applyAuthHeader() {
  const t = getToken();
  if (t) axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
  else delete axios.defaults.headers.common['Authorization'];
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
  applyAuthHeader();
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  applyAuthHeader();
}

export async function login(email: string, password: string): Promise<void> {
  const res = await axios.post(`${API_URL}/auth/login`, { email, password });
  setToken(res.data.token);
}

export function logout() {
  clearToken();
  window.location.href = '/admin/login';
}

// Install the 401 interceptor once. `onUnauthorized` lets the app re-render to
// the login screen when a request is rejected as unauthenticated.
let interceptorInstalled = false;
export function setupAuth(onUnauthorized: () => void) {
  applyAuthHeader();
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      // Don't bounce on a failed login attempt itself.
      const url: string = err?.config?.url || '';
      if (err?.response?.status === 401 && !url.endsWith('/auth/login')) {
        clearToken();
        onUnauthorized();
      }
      return Promise.reject(err);
    }
  );
}

// Attach the header immediately at import time so the very first requests are
// authenticated (before any component effect runs).
applyAuthHeader();
